import { AbstractControl, ValidationErrors, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Component, OnInit } from '@angular/core';
import { ThemingService } from 'src/app/theming.service';
import { Router } from '@angular/router';
import { LambdaService } from 'src/app/lambda.service';
import { ElementRef, ViewChild } from '@angular/core';
import {
  extractShopCompanies,
  ShopCompany,
} from 'src/app/components/shop-companies/shop-company.models';
import {
  getInvalidClientFields,
  InvalidClientField,
  markFormGroupTouched,
} from 'src/app/components/shared/client-form.helpers';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
declare var $: any;

type ClientLoginMethod = 'PASSWORD' | 'PASSWORD_OTP' | 'OTP_ONLY';

const DEFAULT_CLIENT_LOGIN_METHOD: ClientLoginMethod = 'OTP_ONLY';

const CLIENT_LOGIN_METHOD_OPTIONS: Array<{
  labelKey: string;
  value: ClientLoginMethod;
}> = [
  { labelKey: 'CLIENT_LOGIN_PASSWORD', value: 'PASSWORD' },
  { labelKey: 'CLIENT_LOGIN_OTP_ONLY', value: 'OTP_ONLY' },
];

@Component({
  selector: 'app-create-client',
  templateUrl: './create-client.component.html',
  styleUrls: ['./create-client.component.scss']
})
export class CreateClientComponent implements OnInit {
  @ViewChild('clientFormContainer') clientFormContainer?: ElementRef<HTMLElement>;
  loading: boolean = false;
  clientForm: FormGroup;
  showPassword: boolean = false;
  username = '';
  nationalIdInvalid: boolean = false;
  placeholderText: string = '';
  passwordCopied: boolean = false;
  bornDateMax: string = this.getTodayDateString();
  bornDateMaxDate: Date = new Date();
  clientSubscriptions: any[] = [];
  shopCompanies: ShopCompany[] = [];
  loginMethodOptions = CLIENT_LOGIN_METHOD_OPTIONS;
  invalidClientFields: InvalidClientField[] = [];

  constructor(
    public themingService: ThemingService,
    private fb: FormBuilder,
    private router: Router,
    private lambdaService: LambdaService
  ) {
    this.clientForm = this.fb.group({
      firstName: ['', [Validators.required, Validators.minLength(2)]],
      lastName: ['', [Validators.required, Validators.minLength(2)]],
      gender: [''],
      employeeId: [''],
      phone: [''],
      documentType: ['', Validators.required],
      clientSubscription: [''],
      shopCompanyId: [null],
      loginMethod: [{ value: DEFAULT_CLIENT_LOGIN_METHOD, disabled: true }, Validators.required],
      previousLoginMethod: [{ value: '', disabled: true }],
      nationalId: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      password: [{ value: '', disabled: true }],
      bornDate: ['', [this.bornDateValidator]],
      isPoolRegister: [false],
      isVerifiedClient: [false]
    });
  }

  ngOnInit(): void {
    this.getSubscriptions();
    this.getShopCompanies();
    this.handleAccessControlChanges();
    this.clientForm.get('nationalId')?.valueChanges.subscribe(value => {
      this.validateNationalId(value);
    });

    this.clientForm.get('documentType')?.valueChanges.subscribe(type => {
    this.placeholderText = type === 'Passport'
      ? 'A12345678900'
      : (type === 'DIMEX'
        ? '12345678901'
        : (type === 'other'
          ? ''
          : '123456789'));
    this.validateNationalId(this.clientForm.get('nationalId')?.value || '');
  });
  }

  handleAccessControlChanges(): void {
    this.clientForm.get('isPoolRegister')?.valueChanges.subscribe(() => {
      this.updateAccessControls();
    });

    this.clientForm.get('loginMethod')?.valueChanges.subscribe(() => {
      this.updateAccessControls();
    });

    this.updateAccessControls();
  }

  isOnlinePurchasesEnabled(): boolean {
    return this.clientForm.get('isPoolRegister')?.value === true;
  }

  isPasswordLoginSelected(): boolean {
    return this.normalizeLoginMethod(this.clientForm.get('loginMethod')?.value) === 'PASSWORD';
  }

  shouldShowLoginMethod(): boolean {
    return this.isOnlinePurchasesEnabled();
  }

  shouldShowPasswordField(): boolean {
    return this.isOnlinePurchasesEnabled() && this.isPasswordLoginSelected();
  }

  updateAccessControls(): void {
    const loginMethodControl = this.clientForm.get('loginMethod');
    const passwordControl = this.clientForm.get('password');

    if (!loginMethodControl || !passwordControl) {
      return;
    }

    const isPoolRegister = this.isOnlinePurchasesEnabled();
    const loginMethod = this.normalizeLoginMethod(loginMethodControl.value);

    if (!isPoolRegister) {
      loginMethodControl.disable({ emitEvent: false });
      loginMethodControl.setValue(DEFAULT_CLIENT_LOGIN_METHOD, { emitEvent: false });

      passwordControl.disable({ emitEvent: false });
      passwordControl.clearValidators();
      passwordControl.reset('', { emitEvent: false });
      passwordControl.updateValueAndValidity({ emitEvent: false });

      this.showPassword = false;
      this.passwordCopied = false;
      return;
    }

    loginMethodControl.enable({ emitEvent: false });

    if (!loginMethodControl.value) {
      loginMethodControl.setValue(DEFAULT_CLIENT_LOGIN_METHOD, { emitEvent: false });
    }

    if (loginMethod === 'PASSWORD') {
      passwordControl.enable({ emitEvent: false });
      passwordControl.setValidators([Validators.required, this.passwordValidator]);
    } else {
      passwordControl.disable({ emitEvent: false });
      passwordControl.clearValidators();
      passwordControl.reset('', { emitEvent: false });
      this.showPassword = false;
      this.passwordCopied = false;
    }

    passwordControl.updateValueAndValidity({ emitEvent: false });
  }

  bornDateValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!value) {
      return null;
    }

    const selectedDate = value instanceof Date ? new Date(value) : new Date(value);
    if (Number.isNaN(selectedDate.getTime())) {
      return { invalidDate: true };
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    selectedDate.setHours(0, 0, 0, 0);

    return selectedDate > today ? { futureDate: true } : null;
  }

  passwordValidator(control: AbstractControl): ValidationErrors | null {
    const password = control.value;
    if (!password) return null;

    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*]/.test(password);
    const isValidLength = password.length >= 8;

    return !(hasUpperCase && hasLowerCase && hasNumber && hasSpecialChar && isValidLength) ? {
      passwordStrength: {
        hasUpperCase, hasLowerCase, hasNumber, hasSpecialChar, isValidLength
      }
    } : null;
  }

  generatePassword(): void {
    if (this.clientForm.get('password')?.enabled) {
      const length = 12;
      const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
      const lower = 'abcdefghijklmnopqrstuvwxyz';
      const numbers = '0123456789';
      const specials = '!@#$%^&*';
      const allChars = `${upper}${lower}${numbers}${specials}`;

      const basePassword = [
        upper.charAt(Math.floor(Math.random() * upper.length)),
        lower.charAt(Math.floor(Math.random() * lower.length)),
        numbers.charAt(Math.floor(Math.random() * numbers.length)),
        specials.charAt(Math.floor(Math.random() * specials.length)),
      ];

      for (let i = basePassword.length; i < length; i++) {
        basePassword.push(allChars.charAt(Math.floor(Math.random() * allChars.length)));
      }

      const password = basePassword
        .sort(() => Math.random() - 0.5)
        .join('');

      this.clientForm.controls['password'].setValue(password);
      this.passwordCopied = false;
    }
  }

  togglePasswordVisibility(): void {
    if (this.clientForm.get('password')?.enabled) {
      this.showPassword = !this.showPassword;
    }
  }

  onDocumentTypeChange(event: Event): void {
    const documentType = (event.target as HTMLSelectElement).value;
    this.clientForm.get('documentType')?.setValue(documentType);
    this.validateNationalId(this.clientForm.get('nationalId')?.value || '');
  }

  validateNationalId(value: string): void {
    value = value.trim();
    const documentType = this.clientForm.get('documentType')?.value;
    const nationalIdControl = this.clientForm.get('nationalId');
    if (!nationalIdControl) {
      return;
    }

    if (!value) {
      this.nationalIdInvalid = false;
      this.removeCustomError(nationalIdControl, 'invalid');
      return;
    }

    if (documentType === 'ID') {
      const isNumeric = /^\d+$/.test(value);
      const isValidLength = value.length === 9;
      this.nationalIdInvalid = !isNumeric || !isValidLength;
    } else if (documentType === 'Passport') {
      this.nationalIdInvalid = !/^[A-Z][0-9]{8,11}$/.test(value);
    } else if (documentType === 'DIMEX') {
      this.nationalIdInvalid = !/^[1-9][0-9]{11}$/.test(value);
    } else if (documentType === 'other') {
      this.nationalIdInvalid = value.length === 0;
    } else {
      this.nationalIdInvalid = false;
    }

    if (this.nationalIdInvalid) {
      nationalIdControl.setErrors({ ...(nationalIdControl.errors || {}), invalid: true });
    } else {
      this.removeCustomError(nationalIdControl, 'invalid');
    }
  }

  async getSubscriptions() {
    this.loading = true;
    try {
      let response: any = await this.lambdaService.getClientSubscriptions(this.themingService.shopId);
      this.clientSubscriptions = response.subscriptions;
      this.loading = false;
    } catch (error) {
      console.error(error);
      this.loading = false;
    }
  }

  async getShopCompanies() {
    try {
      const response: any = await this.lambdaService.getShopCompanies(
        this.themingService.shopId,
        true
      );
      this.shopCompanies = extractShopCompanies(response).filter(
        (company) => company.status === 1
      );
    } catch (error) {
      console.error('Error loading shop companies:', error);
      this.shopCompanies = [];
    }
  }

  createClient(): void {
    if (!this.validateClientFormBeforeSubmit()) {
      return;
    }

    this.loading = true;
    const clientData = this.clientForm.getRawValue();
    clientData.isVerifiedClient = clientData.isVerifiedClient ? 1 : 0;
    const shopCompanyId = this.normalizeShopCompanyId(
      clientData.shopCompanyId
    );
    const loginMethod = this.normalizeLoginMethod(clientData.loginMethod);
    const isPoolRegister = clientData.isPoolRegister === true;
    const password = isPoolRegister && loginMethod === 'PASSWORD'
      ? clientData.password
      : '';

    this.lambdaService.createClient(
      this.themingService.shopId,
      clientData.firstName,
      clientData.lastName,
      this.normalizePhoneValue(clientData.phone),
      clientData.nationalId,
      clientData.employeeId || "",
      clientData.gender || "",
      clientData.email,
      this.username || '',
      isPoolRegister,
      password,
      clientData.isVerifiedClient,
      clientData.documentType,
      clientData.clientSubscription,
      shopCompanyId ?? undefined,
      loginMethod,
      clientData.previousLoginMethod || '',
      this.formatDateForPayload(clientData.bornDate)
    ).then((data: any) => {
      this.loading = false;
      if (data.success === true) {
        $('#successClientModal').modal('show');
        $('#successClientModal').on('hidden.bs.modal', () => {
          this.clientForm.reset();
          this.router.navigate(['/clients']);
        });
      } else {
        this.showErrorModal();
      }
    }).catch((error) => {
      console.log(error);
      this.loading = false;
      this.showErrorModal();
    });
  }

  async copyPassword(): Promise<void> {
    const password = this.clientForm.get('password')?.value;
    if (!password) {
      return;
    }

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(password);
      } else {
        const helperInput = document.createElement('textarea');
        helperInput.value = password;
        helperInput.style.position = 'fixed';
        helperInput.style.left = '-9999px';
        document.body.appendChild(helperInput);
        helperInput.select();
        document.execCommand('copy');
        document.body.removeChild(helperInput);
      }
      this.passwordCopied = true;
      setTimeout(() => {
        this.passwordCopied = false;
      }, 2000);
    } catch (error) {
      console.error('Failed to copy password:', error);
    }
  }

  private validateClientFormBeforeSubmit(): boolean {
    markFormGroupTouched(this.clientForm);
    this.invalidClientFields = getInvalidClientFields(this.clientForm);

    if (this.clientForm.invalid) {
      this.showRequiredFieldsModal();
      this.focusFirstInvalidControl();
      return false;
    }

    this.invalidClientFields = [];
    return true;
  }

  private focusFirstInvalidControl(): void {
    const firstInvalidControlName = Object.keys(this.clientForm.controls).find(
      (controlName) => this.clientForm.get(controlName)?.invalid
    );

    if (!firstInvalidControlName) {
      return;
    }

    const hostElement = this.clientFormContainer?.nativeElement || document;
    const invalidElement = hostElement.querySelector(
      `[formControlName="${firstInvalidControlName}"]`
    ) as HTMLElement | null;

    if (invalidElement) {
      invalidElement.focus();
      invalidElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  private removeCustomError(control: AbstractControl, errorKey: string): void {
    const errors = control.errors;
    if (!errors || !errors[errorKey]) {
      return;
    }
    const { [errorKey]: _, ...remainingErrors } = errors;
    control.setErrors(Object.keys(remainingErrors).length ? remainingErrors : null);
  }

  private getTodayDateString(): string {
    return new Date().toISOString().split('T')[0];
  }

  private formatDateForPayload(value: unknown): string | undefined {
    if (!value) {
      return undefined;
    }

    const date = value instanceof Date ? value : new Date(value as string);
    if (Number.isNaN(date.getTime())) {
      return undefined;
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  showErrorModal(): void {
    $('#errorClientModal').modal('show');
    $('#errorClientModal').on('hidden.bs.modal', () => {
      this.clientForm.markAsPristine();
    });
  }

  showRequiredFieldsModal(): void {
    $('#requiredClientModal').modal('show');
  }

  private normalizeShopCompanyId(value: unknown): number | undefined {
    if (value === null || value === undefined || value === '') {
      return undefined;
    }
    const id = Number(value);
    return Number.isFinite(id) ? id : undefined;
  }

  private normalizeLoginMethod(value: unknown): ClientLoginMethod {
    return this.loginMethodOptions.some((option) => option.value === value)
      ? (value as ClientLoginMethod)
      : DEFAULT_CLIENT_LOGIN_METHOD;
  }

  private normalizePhoneValue(value: unknown): string {
    if (!value || typeof value !== 'string') {
      return '';
    }

    const phoneNumber =
      parsePhoneNumberFromString(value) ||
      parsePhoneNumberFromString(value, 'CR');

    return phoneNumber?.number || value;
  }

  closeModal() {
    $('#successClientModal').modal('hide');
    $('#errorClientModal').modal('hide');
    $('#requiredClientModal').modal('hide');
  }
}
